import { useEffect, useMemo, useRef, useState } from 'react';
import { binaryMaskFromImageData } from './binaryMaskFromImageData';
import { blendMasksThreshold } from './blendMasksThreshold';
import { circleChordSlatRects } from './circleChordSlatRects';
import { maskToSlatRects } from './imageDataToSlatRects';
import {
  drawImageContainedToGrid,
  loadImageElement,
  unifiedGridForImages,
} from './loadImageToMask';

const defaultViewBox = { width: 200, height: 200 };

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * Map overall morph progress t ∈ [0,1] (0 = platform A, 1 = Europe B) to mask blend and row count.
 *
 * **Sequential two-leg morph** (smooth shape read):
 * - **t ∈ [0, ½]**: stay on mask A only (`blendT = 0`); row count goes `rowStart → rowMid` (coarse circle).
 * - **t ∈ [½, 1]**: blend `0 → 1` toward B while row count goes `rowMid → rowEnd` (Europe resolves).
 *
 * This avoids Europe appearing as soon as hover starts; the circle collapses to few rows first, then morphs.
 *
 * @param {number} tBlend
 * @param {number} rowStart
 * @param {number} rowMid
 * @param {number} rowEnd
 */
export function mapMorphProgressToBlendAndRows(tBlend, rowStart, rowMid, rowEnd) {
  const t = Math.min(1, Math.max(0, tBlend));
  const rs = rowStart;
  const rm = rowMid;
  const re = rowEnd;

  if (t <= 0.5) {
    const s = t / 0.5;
    return {
      blendT: 0,
      rowR: Math.max(1, Math.round(rs + (rm - rs) * s)),
    };
  }
  const s = (t - 0.5) / 0.5;
  return {
    blendT: s,
    rowR: Math.max(1, Math.round(rm + (re - rm) * s)),
  };
}

/**
 * @param {HTMLImageElement} imgA
 * @param {HTMLImageElement} imgB
 * @param {number} rowR
 * @param {object} classifyOptions
 * @param {boolean} smoothing
 */
function buildPairMasks(imgA, imgB, rowR, classifyOptions, smoothing) {
  const r = Math.max(1, Math.round(rowR));
  const { gridWidth, gridHeight } = unifiedGridForImages(imgA, imgB, r);
  const dataA = drawImageContainedToGrid(imgA, gridWidth, gridHeight, { smoothing });
  const dataB = drawImageContainedToGrid(imgB, gridWidth, gridHeight, { smoothing });
  const { mask: a } = binaryMaskFromImageData(dataA, classifyOptions);
  const { mask: b } = binaryMaskFromImageData(dataB, classifyOptions);
  return { a, b, gridW: gridWidth, gridH: gridHeight };
}

/**
 * Slat morph: blend masks + re-extract rects each frame.
 * Uses one rAF loop keyed only on `imagesReady` so `active` / layout option identity changes
 * do not cancel the animation mid-flight (which caused instant snaps).
 *
 * @param {string | null} srcA
 * @param {string | null} srcB
 * @param {object} [options]
 * @param {number} [options.rowCount=56]
 * @param {number} [options.morphRowCountFrom]
 * @param {number} [options.morphRowCountTo]
 * @param {number} [options.morphMidRowCount=3] - row count at end of leg 1 / start of leg 2 (t=0.5)
 * @param {boolean} [options.twoPhaseMorph=true] - leg1: shape A, rows high→mid (blend 0); leg2: blend 0→1, rows mid→end
 * @param {boolean} [options.active=false]
 * @param {number} [options.durationMs=900]
 * @param {{ width: number, height: number }} [options.viewBox]
 * @param {number} [options.barFillRatio]
 * @param {number} [options.padding]
 * @param {number} [options.threshold]
 * @param {boolean} [options.invert]
 * @param {number} [options.minRunWidthPx]
 * @param {boolean} [options.smoothing]
 * @param {() => void} [options.onComplete]
 * @param {{ r: number }} [options.analyticCircleSlats] - when set, blendT === 0 uses exact circle chords (same r as mask SVG), avoiding raster warp from the Europe grid
 */
export function useSlatMorphBetweenImages(srcA, srcB, options = {}) {
  const rowFallback = options.rowCount ?? 56;
  const rowStart = options.morphRowCountFrom ?? rowFallback;
  const rowEnd = options.morphRowCountTo ?? rowFallback;
  const rowMid = options.morphMidRowCount ?? 3;
  const twoPhaseMorph = options.twoPhaseMorph !== false;
  const durationMs = options.durationMs ?? 900;
  const active = options.active ?? false;
  const viewBox = options.viewBox ?? defaultViewBox;
  const smoothing = options.smoothing ?? false;
  const analyticCircleR = options.analyticCircleSlats?.r;

  const onCompleteRef = useRef(options.onComplete);
  useEffect(() => {
    onCompleteRef.current = options.onComplete;
  }, [options.onComplete]);

  const classifyOptions = useMemo(
    () => ({
      threshold: options.threshold,
      invert: options.invert,
      alphaCutoff: options.alphaCutoff,
    }),
    [options.threshold, options.invert, options.alphaCutoff],
  );

  const layoutOptions = useMemo(
    () => ({
      barFillRatio: options.barFillRatio,
      padding: options.padding,
      opacity: options.opacity,
      minRunWidthPx: options.minRunWidthPx,
    }),
    [options.barFillRatio, options.padding, options.opacity, options.minRunWidthPx],
  );

  const [state, setState] = useState({
    status: 'idle',
    rects: [],
    error: null,
    phase: 'a',
  });

  const [imagesReady, setImagesReady] = useState(false);

  const imgsRef = useRef(null);
  const progressRef = useRef(0);

  const activeRef = useRef(active);
  const durationMsRef = useRef(durationMs);
  const rowStartRef = useRef(rowStart);
  const rowMidRef = useRef(rowMid);
  const rowEndRef = useRef(rowEnd);
  const twoPhaseMorphRef = useRef(twoPhaseMorph);
  const layoutOptionsRef = useRef(layoutOptions);
  const classifyOptionsRef = useRef(classifyOptions);
  const viewBoxRef = useRef(viewBox);
  const smoothingRef = useRef(smoothing);
  const analyticCircleSlatsRef = useRef(options.analyticCircleSlats ?? null);

  activeRef.current = active;
  durationMsRef.current = durationMs;
  rowStartRef.current = rowStart;
  rowMidRef.current = rowMid;
  rowEndRef.current = rowEnd;
  twoPhaseMorphRef.current = twoPhaseMorph;
  layoutOptionsRef.current = layoutOptions;
  classifyOptionsRef.current = classifyOptions;
  viewBoxRef.current = viewBox;
  smoothingRef.current = smoothing;
  analyticCircleSlatsRef.current = options.analyticCircleSlats ?? null;

  useEffect(() => {
    if (!srcA || !srcB) {
      imgsRef.current = null;
      setImagesReady(false);
      setState({ status: 'idle', rects: [], error: null, phase: 'a' });
      return undefined;
    }

    let cancelled = false;
    setImagesReady(false);
    setState((s) => ({ ...s, status: 'loading' }));

    (async () => {
      try {
        const [imgA, imgB] = await Promise.all([loadImageElement(srcA), loadImageElement(srcB)]);
        if (cancelled) return;

        imgsRef.current = { imgA, imgB };
        progressRef.current = 0;

        const spec = analyticCircleSlatsRef.current;
        let rects;
        if (spec && typeof spec.r === 'number' && spec.r > 0) {
          rects = circleChordSlatRects(viewBox, layoutOptions, rowStart, spec);
        } else {
          const { a, gridW, gridH } = buildPairMasks(
            imgA,
            imgB,
            rowStart,
            classifyOptions,
            smoothing,
          );
          rects = maskToSlatRects(a, gridW, gridH, viewBox, layoutOptions).rects;
        }

        if (cancelled) return;
        setState({
          status: 'ready',
          rects,
          error: null,
          phase: 'a',
        });
        setImagesReady(true);
      } catch (err) {
        if (cancelled) return;
        imgsRef.current = null;
        setImagesReady(false);
        setState({
          status: 'error',
          rects: [],
          error: err instanceof Error ? err : new Error(String(err)),
          phase: 'a',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    srcA,
    srcB,
    rowStart,
    rowEnd,
    classifyOptions,
    layoutOptions,
    viewBox.width,
    viewBox.height,
    smoothing,
    analyticCircleR,
  ]);

  useEffect(() => {
    if (!imagesReady || !imgsRef.current?.imgA || !imgsRef.current?.imgB) {
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;

    const anim = {
      lastWantT: null,
      segmentWall: 0,
      fromT: 0,
      toT: 0,
      segmentPrevU: 0,
    };

    const tick = (now) => {
      if (cancelled) return;

      const wantT = activeRef.current ? 1 : 0;
      if (anim.lastWantT !== wantT) {
        anim.lastWantT = wantT;
        anim.segmentWall = now;
        anim.fromT = progressRef.current;
        anim.toT = wantT;
        anim.segmentPrevU = 0;
      }

      const dur = durationMsRef.current;
      const elapsed = now - anim.segmentWall;
      const u = dur <= 0 ? 1 : Math.min(1, elapsed / dur);
      const eased = easeInOutCubic(u);
      const t = anim.fromT + (anim.toT - anim.fromT) * eased;
      progressRef.current = t;

      const rs = rowStartRef.current;
      const rm = rowMidRef.current;
      const re = rowEndRef.current;
      const useTwoPhase = twoPhaseMorphRef.current;
      const { blendT, rowR } = useTwoPhase
        ? mapMorphProgressToBlendAndRows(t, rs, rm, re)
        : {
            blendT: t,
            rowR: Math.max(1, Math.round(rs + (re - rs) * t)),
          };

      const circleSpec = analyticCircleSlatsRef.current;
      if (circleSpec && typeof circleSpec.r === 'number' && circleSpec.r > 0 && blendT <= 1e-9) {
        const rects = circleChordSlatRects(
          viewBoxRef.current,
          layoutOptionsRef.current,
          rowR,
          circleSpec,
        );
        setState((s) => ({
          ...s,
          status: 'ready',
          rects,
          phase: u >= 1 ? (wantT === 1 ? 'b' : 'a') : 'morph',
        }));
        if (anim.segmentPrevU < 1 && u >= 1 && onCompleteRef.current) {
          onCompleteRef.current();
        }
        anim.segmentPrevU = u;
        if (u >= 1) {
          progressRef.current = wantT;
        }
        rafId = requestAnimationFrame(tick);
        return;
      }

      const { imgA, imgB } = imgsRef.current;
      const { a, b, gridW, gridH } = buildPairMasks(
        imgA,
        imgB,
        rowR,
        classifyOptionsRef.current,
        smoothingRef.current,
      );
      const blended = blendMasksThreshold(a, b, blendT);
      const { rects } = maskToSlatRects(
        blended,
        gridW,
        gridH,
        viewBoxRef.current,
        layoutOptionsRef.current,
      );

      setState((s) => ({
        ...s,
        status: 'ready',
        rects,
        phase: u >= 1 ? (wantT === 1 ? 'b' : 'a') : 'morph',
      }));

      if (anim.segmentPrevU < 1 && u >= 1 && onCompleteRef.current) {
        onCompleteRef.current();
      }
      anim.segmentPrevU = u;

      if (u >= 1) {
        progressRef.current = wantT;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [imagesReady]);

  return state;
}
